import {
  OnChangeFn,
  Table,
  ColumnOrderState,
  RowSelectionState,
} from '@tanstack/react-table'
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'
import { IColumnSort, IColumnVisibility, IFilterCriteria } from './types'
import useCachedState from '../../utils/useCachedState'
import { useAuth } from '../../context/authentication/useAuth'

const DataTableContext = createContext<DataTableContextData<object> | null>(
  null
)

export function DataTableProvider<T>({
  children,
  tableKey,
  defaultColumnVisibility,
  hasNote,
}: DataTableProviderProps<T>) {
  const { user } = useAuth()
  const LOCAL_STORAGE_KEY_PREFIX = `5-${user?.id}-${tableKey}`

  // This is a copy of the table
  const [table, setTable] = useState<Table<object>>()

  const [expanded, setExpanded] = React.useState<string[]>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const clearSelectedRows = () => {
    setRowSelection({})
    setIsAllRowsSelected(false)
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sorting, setSorting] = useCachedState<IColumnSort<any>[]>(
    [],
    LOCAL_STORAGE_KEY_PREFIX + '-sorting'
  )

  const [isAllRowsSelected, setIsAllRowsSelected] = useState<boolean>(false)
  const [totalRowsInBackend, setTotalRowsInBackend] = useState<number>(0)

  const [columnVisibility, setColumnVisibility] = useCachedState<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    IColumnVisibility<any> | undefined
  >(defaultColumnVisibility, LOCAL_STORAGE_KEY_PREFIX + '-column-visibility')
  const [columnOrder, setColumnOrder] = useCachedState<ColumnOrderState>(
    [],
    LOCAL_STORAGE_KEY_PREFIX + '-column-order'
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSort = (columnId: any) => {
    const isSorted = sorting?.find((s) => s.id === columnId)

    if (isSorted == null) {
      setSorting([{ id: columnId, desc: true }])
      return
    }

    if (isSorted.desc) {
      setSorting([{ id: columnId, desc: false }])
      return
    }

    if (!isSorted.desc) {
      setSorting([])
      return
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function toggleVisibility(columnId: any) {
    const newVisibility = { ...columnVisibility }

    // eslint-disable-next-line no-prototype-builtins
    if (newVisibility.hasOwnProperty(columnId)) {
      newVisibility[columnId] = !newVisibility[columnId]
    } else {
      newVisibility[columnId] = false
    }

    setColumnVisibility(newVisibility)
  }

  const totalSelectedRows = useMemo(() => {
    if (isAllRowsSelected) {
      let total = totalRowsInBackend
      // Exclude unselected rows from the total
      if (table) {
        const { rows: totalRows } = table.getRowModel()
        const selectedRowsCount = Object.keys(rowSelection).length
        total = total - (totalRows.length - selectedRowsCount)
      }
      return total
    } else {
      return Object.keys(rowSelection).length
    }
  }, [rowSelection, isAllRowsSelected, totalRowsInBackend])

  return (
    <DataTableContext.Provider
      value={{
        methods: {
          setTable: setTable,
          setSorting: setSorting,
          toggleVisibility: toggleVisibility,
          handleSort: handleSort,
          setColumnOrder: setColumnOrder,
          setColumnVisibility: setColumnVisibility,
          setRowSelection,
          clearSelectedRows,
          setExpanded: setExpanded,
          setIsAllRowsSelected,
          setTotalRowsInBackend,
        },
        state: {
          tableKey: tableKey,
          table: table,
          sorting: sorting,
          columnVisibility: columnVisibility,
          isAllRowsSelected: isAllRowsSelected,
          hasNote: hasNote,
          columnOrder: columnOrder,
          rowSelection,
          expanded,
          totalSelectedRows,
        },
      }}
    >
      {children}
    </DataTableContext.Provider>
  )
}

type DataTableContextData<T extends object> = {
  methods: {
    setTable: (table: Table<T>) => void
    setSorting: OnChangeFn<IColumnSort<T>[]>
    toggleVisibility: (columnId: keyof T) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleSort: (columnId: any) => void
    setColumnOrder: Dispatch<SetStateAction<ColumnOrderState>>
    setColumnVisibility: Dispatch<
      SetStateAction<IColumnVisibility<T> | undefined>
    >
    setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
    clearSelectedRows: () => void
    setExpanded: React.Dispatch<React.SetStateAction<string[]>>
    setIsAllRowsSelected: Dispatch<SetStateAction<boolean>>
    setTotalRowsInBackend: Dispatch<SetStateAction<number>>
  }
  state: {
    tableKey: string
    table?: Table<T>
    sorting?: IColumnSort<T>[]
    columnVisibility?: IColumnVisibility<T>
    isAllRowsSelected: boolean
    hasNote?: boolean
    columnOrder?: ColumnOrderState
    rowSelection: RowSelectionState
    expanded: string[]
    totalSelectedRows: number
  }
}

type DataTableProviderProps<T> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any
  hasNote?: boolean
  tableKey: string
  defaultColumnVisibility?: IColumnVisibility<T>
  defaultFilterCriteria?: IFilterCriteria
}

export function useDataTableContext<T extends object>() {
  const context = useContext(
    DataTableContext as unknown as React.Context<DataTableContextData<T>>
  )

  if (!context) {
    throw new Error(
      'useDataTableContext must be used within a DataTableProvider'
    )
  }

  return { ...context }
}
